import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import { container } from '@infrastructure/container';
import { useAuth } from '@shared/hooks';
import { useAuthStore } from '../../../application/store/AuthStore';
import {
    storePendingShopifyInstall,
    getPendingShopifyInstall,
    clearPendingShopifyInstall,
} from '../../../features/connections/shopify/pendingInstall';

/**
 * Landing page for Shopify App Store installs. Shopify redirects the merchant here
 * (after OAuth) with `shop` and `install_token`. If the merchant is already logged
 * in we immediately claim the parked install and create their Shopify connection;
 * otherwise we stash the params and send them through sign-in / sign-up, resuming
 * the claim once they return authenticated.
 */
const ShopifyInstallSignUp: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const companyId = useAuthStore((state) => state.companyId);
    const jwt = useAuthStore((state) => state.jwt);
    const hasClaimed = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Persist the install params so they survive a sign-in / sign-up redirect.
    useEffect(() => {
        const shop = searchParams.get('shop');
        const installToken = searchParams.get('install_token');
        if (shop && installToken) {
            storePendingShopifyInstall({ shop, installToken });
        }
    }, [searchParams]);

    useEffect(() => {
        if (authLoading || hasClaimed.current) return;

        const stored = getPendingShopifyInstall();
        if (!stored) {
            setError('Missing install information. Please reinstall JustStock from the Shopify App Store.');
            setIsLoading(false);
            return;
        }

        // AuthContext may lag one render behind AuthStore after company select.
        const canClaim = isAuthenticated || Boolean(companyId && jwt);

        if (!canClaim) {
            // Send the merchant into the sign-up / onboarding flow. New accounts run
            // email → validate → password, and SignUpPassword routes back here to
            // finish the Shopify connect + billing (skipping the marketplaces grid).
            // Existing users can switch to sign-in from the email step.
            navigate('/sign-up/email');
            return;
        }

        hasClaimed.current = true;
        const { shop, installToken } = stored;

        container.shopifyConnectorRepository
            .claimShopifyInstall(shop, installToken)
            .then(async (response) => {
                if (response.status === 'error') {
                    setError(response.error?.message || 'Failed to finish the Shopify connection');
                    setIsLoading(false);
                    return;
                }
                clearPendingShopifyInstall();

                const trial = await container.subscriptionRepository.startShopifyTrial();
                if (trial.status === 'error') {
                    setError(trial.error?.message ?? 'Failed to start your trial');
                    setIsLoading(false);
                    return;
                }

                navigate(`/company/connections/${response.data.connectionId}/setup`);
            })
            .catch((err) => {
                setError(err?.message || 'Failed to finish the Shopify connection');
                setIsLoading(false);
            });
    }, [authLoading, isAuthenticated, companyId, jwt, navigate]);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 0
        }}>
            <DialogContainer maxWidth="600px">
                <Box sx={{ position: 'relative', margin: 4 }}>
                    <Box sx={{ textAlign: 'left', position: 'relative' }}>
                        <img src="/assets/images/ic_just_stock.png" alt="JustStock Logo" style={{ height: 16 }} />
                    </Box>

                    {isLoading && !error && (
                        <Stack alignItems="center" spacing={2} sx={{ height: '200px', justifyContent: 'center' }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Finishing Shopify connection…</Typography>
                        </Stack>
                    )}

                    {error && (
                        <Box sx={{ pt: 3 }}>
                            <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                                <Typography color="error.main">{error}</Typography>
                            </Box>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button variant="outlined" onClick={() => navigate('/')}>Back to home</Button>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </DialogContainer>
        </Box>
    );
};

export default ShopifyInstallSignUp;
